import React, { useState } from "react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";

const CARD_SIZE = 1024;
const AVATAR = { left: 0.306, top: 0.361, size: 0.359 };
const NAME_BAR = { left: 0.259, top: 0.753, width: 0.459, height: 0.076 };
const DATE_POS = { right: 0.063, top: 0.79 };

const PRIMARY = "#000c66";
const GOLD = "#ffc000";

const ordinalDob = (dob) => {
  if (!dob) return "";
  const [day, month] = dob.trim().split(" ");
  const dayNum = parseInt(day, 10);
  if (!dayNum) return dob;
  const suffix =
    dayNum % 100 >= 11 && dayNum % 100 <= 13
      ? "th"
      : ["th", "st", "nd", "rd"][dayNum % 10] || "th";
  return `${dayNum}${suffix}${month ? ` ${month}` : ""}`;
};

// Mirrors CSS `object-fit: cover` for canvas — crops the source to the
// target's aspect ratio instead of stretching it, so non-square photos
// don't come out distorted in the downloaded PNG.
const drawImageCover = (ctx, img, dx, dy, dWidth, dHeight) => {
  const targetRatio = dWidth / dHeight;
  const sourceRatio = img.width / img.height;
  let sx, sy, sWidth, sHeight;
  if (sourceRatio > targetRatio) {
    sHeight = img.height;
    sWidth = sHeight * targetRatio;
    sx = (img.width - sWidth) / 2;
    sy = 0;
  } else {
    sWidth = img.width;
    sHeight = sWidth / targetRatio;
    sx = 0;
    sy = (img.height - sHeight) / 2;
  }
  ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// A plain <img src={imgSrc}> is already rendered on-screen with no CORS mode,
// so the browser may have cached an opaque response for that URL. Re-fetching
// the same URL in CORS mode can be served that cached opaque response and
// still taint the canvas — cache-busting forces a fresh, CORS-validated fetch.
const loadImageForCanvas = async (src) => {
  const cacheBustUrl = `${src}${src.includes("?") ? "&" : "?"}cb=${Date.now()}`;
  const res = await fetch(cacheBustUrl, { mode: "cors", cache: "no-store" });
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return loadImage(dataUrl);
};

export const BirthdayCard = ({ name, imgSrc, dob }) => {
  const [downloading, setDownloading] = useState(false);
  const dateLabel = ordinalDob(dob);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = CARD_SIZE;
      canvas.height = CARD_SIZE;
      const ctx = canvas.getContext("2d");

      const bg = await loadImage("/hbd3.png");
      ctx.drawImage(bg, 0, 0, CARD_SIZE, CARD_SIZE);

      const ax = AVATAR.left * CARD_SIZE;
      const ay = AVATAR.top * CARD_SIZE;
      const asize = AVATAR.size * CARD_SIZE;

      let avatarImg = null;
      if (imgSrc) {
        try {
          avatarImg = await loadImageForCanvas(imgSrc);
        } catch {
          toast("Your photo couldn't be included in the download.", {
            type: "warning",
          });
        }
      }

      ctx.save();
      ctx.beginPath();
      ctx.arc(ax + asize / 2, ay + asize / 2, asize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (avatarImg) {
        drawImageCover(ctx, avatarImg, ax, ay, asize, asize);
      } else {
        ctx.fillStyle = PRIMARY;
        ctx.fillRect(ax, ay, asize, asize);
        ctx.fillStyle = GOLD;
        ctx.font = "700 100px Abel, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(initials(name), ax + asize / 2, ay + asize / 2 + 8);
      }
      ctx.restore();

      if (document.fonts?.load) {
        try {
          await document.fonts.load("900 46px Abel");
          await document.fonts.load("800 40px Abel");
        } catch {
          // fall back to default font metrics if Abel hasn't loaded yet
        }
      }

      ctx.fillStyle = PRIMARY;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "900 46px Abel, sans-serif";
      ctx.fillText(
        name || "",
        (NAME_BAR.left + NAME_BAR.width / 2) * CARD_SIZE,
        (NAME_BAR.top + NAME_BAR.height / 2) * CARD_SIZE,
        NAME_BAR.width * CARD_SIZE - 24,
      );

      if (dateLabel) {
        ctx.fillStyle = "#fff";
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.font = "800 40px Abel, sans-serif";
        ctx.fillText(
          `✦ ${dateLabel}`,
          (1 - DATE_POS.right) * CARD_SIZE,
          DATE_POS.top * CARD_SIZE,
        );
      }

      const link = document.createElement("a");
      link.download = `${(name || "birthday").trim().replace(/\s+/g, "-").toLowerCase()}-birthday-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      toast("Couldn't generate the download. Please try again.", {
        type: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          aspectRatio: "1 / 1",
          margin: "0 auto",
          backgroundImage: "url(/hbd3.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: `${AVATAR.left * 100}%`,
            top: `${AVATAR.top * 100}%`,
            width: `${AVATAR.size * 100}%`,
            height: `${AVATAR.size * 100}%`,
            borderRadius: "50%",
            overflow: "hidden",
            background: PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span
              style={{
                fontFamily: "Abel",
                fontSize: "clamp(24px, 8vw, 40px)",
                color: GOLD,
                fontWeight: 700,
              }}
            >
              {initials(name)}
            </span>
          )}
        </div>

        <div
          style={{
            position: "absolute",
            left: `${NAME_BAR.left * 100}%`,
            top: `${NAME_BAR.top * 100}%`,
            width: `${NAME_BAR.width * 100}%`,
            height: `${NAME_BAR.height * 100}%`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 4%",
          }}
        >
          <span
            style={{
              fontFamily: "Abel",
              fontWeight: 900,
              color: PRIMARY,
              fontSize: "clamp(14px, 4.6vw, 23px)",
              lineHeight: 1.1,
              letterSpacing: 0.3,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {name}
          </span>
        </div>

        {dateLabel && (
          <div
            style={{
              position: "absolute",
              right: `${DATE_POS.right * 100}%`,
              top: `${DATE_POS.top * 100}%`,
              transform: "translateY(-50%)",
            }}
          >
            <span
              style={{
                fontFamily: "Abel",
                fontWeight: 800,
                color: "#fff",
                fontSize: "clamp(12px, 3.6vw, 19px)",
                textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                whiteSpace: "nowrap",
              }}
            >
              &#10022; {dateLabel}
            </span>
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          variant="contained"
          style={{
            fontFamily: "Abel",
            background: PRIMARY,
            color: GOLD,
            padding: "8px 24px",
          }}
        >
          {downloading ? "Preparing..." : "Download Card"}
        </Button>
      </div>
    </div>
  );
};
